import React, { useState, useEffect } from 'react';
import { 
  FileText, BarChart2, Calendar, Share2, 
  Trash2, Clock, Calendar as CalendarIcon, FilePlus, Tag, Check, Search, Save, X,
  MoreVertical, Sparkles, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  List as ListIcon, ListOrdered, Link, Image as ImageIcon, Table, Code, ChevronLeft
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import RichTextEditor from '../components/RichTextEditor';
import { usePortfolio } from '../context/PortfolioContext';
import { useCexAccount, ALL_ACCOUNTS_ID } from '../context/CexAccountContext';
import { apiService } from '../services/apiService';
import { ProductTour, TourStep } from '../components/ProductTour';

interface NotebookPageProps {
  formData: any;
}

interface JournalEntry {
  id: number;
  trade_date: string;
  remarks: string;
  screenshots: string[];
  mood?: string;
  pnl?: number;
  trades_count?: number;
  created_at: string;
  updated_at: string;
  title?: string;
  tags?: string[];
  folder?: string;
  cex_account_id?: number;
}

// ----------------------------------------------------
// DEFAULT MOCK NOTES FROM FIGMA
// ----------------------------------------------------
const DEFAULT_MOCK_NOTES: JournalEntry[] = [
  {
    id: 9901,
    trade_date: "2026-05-25",
    title: "BTC Trade Recap",
    remarks: `<h2>Market Context</h2>
<p>The Bitcoin market opened with high volatility today following the CPI data release. We saw a sharp rejection at the $66,500 level, which previously acted as a strong supply zone. The 4H timeframe shows a bearish engulfing candle, yet the volume on the dip remains lower than the breakout volume.</p>`,
    created_at: "2026-05-25T09:45:00Z",
    updated_at: "2026-05-25T14:15:00Z",
    mood: "Confident",
    pnl: 2050.00,
    trades_count: 2,
    screenshots: [],
    tags: ["RECAP", "BITCOIN"],
    folder: "trade"
  },
  {
    id: 9902,
    trade_date: "2026-05-24",
    title: "5/14 Daily Journal",
    remarks: `<p>Woke up at 6 AM. Market sentiment is fearful. Crypto Fear & Greed Index is at 38. The price action indicates consolidation inside a narrow wedge. Decided to sit on my hands and wait for a clear breakout before committing any size.</p>`,
    created_at: "2026-05-24T06:00:00Z",
    updated_at: "2026-05-24T06:30:00Z",
    mood: "Neutral",
    pnl: 0,
    trades_count: 0,
    screenshots: [],
    tags: ["RECAP"],
    folder: "daily"
  },
  {
    id: 9903,
    trade_date: "2026-05-22",
    title: "Untitled",
    remarks: `<p>No content available...</p>`,
    created_at: "2026-05-22T12:00:00Z",
    updated_at: "2026-05-22T12:00:00Z",
    mood: "",
    pnl: undefined,
    trades_count: 0,
    screenshots: [],
    tags: [],
    folder: "daily"
  },
  {
    id: 9904,
    trade_date: "2026-05-12",
    title: "Ethereum London Upgrade Analysis",
    remarks: `<p>Breaking down EIP-1559 and what it means for gas run. Burning mechanism explained. I traded the volatility post-upgrade and took some heavy slippage due to network congestion.</p>`,
    created_at: "2026-05-12T10:00:00Z",
    updated_at: "2026-05-12T11:00:00Z",
    mood: "Frustrated",
    pnl: -450,
    trades_count: 1,
    screenshots: [],
    tags: ["LOSS"],
    folder: "trade"
  },
  {
    id: 9905,
    trade_date: "2026-05-10",
    title: "Swing Trade: SOL/USDT",
    remarks: `<p>Bullish divergence on the 4H chart. Long position initiated at $142.30. Target at $155.00. Stop Loss set just below the local swing low at $138.50.</p>`,
    created_at: "2026-05-10T15:00:00Z",
    updated_at: "2026-05-10T18:00:00Z",
    mood: "Patient",
    pnl: 850,
    trades_count: 1,
    screenshots: [],
    tags: ["BREAKOUT"],
    folder: "trade"
  }
];

import { useSEO } from '../hooks/useSEO';
import { useTheme } from '../hooks/useTheme';

const NotebookPage: React.FC<NotebookPageProps> = ({ formData }) => {
  useSEO({
    title: 'Trading Notebook',
    description: 'Catat rencana trading Anda, pengamatan pasar, pelajaran berharga, dan pedoman emosi di buku catatan trading interaktif.',
    keywords: 'trading notebook, crypto journal, markdown editor, trading plans, trading journal'
  });

  const { selectedAccount } = useCexAccount();
  const isDarkMode = useTheme();

  const tourSteps: TourStep[] = [
    {
      target: 'body',
      title: "Daily Trading Journal (Notebook)!",
      content: "Recording emotions and daily setup notes is the key to long-term success. Write your daily journal here.",
      position: 'center'
    },
    {
      target: '[data-tour="notebook-folders"]',
      title: "Folder Filter",
      content: "Group your notes by type: all notes, trade performance notes, or daily journals.",
      position: 'right'
    },
    {
      target: '[data-tour="notebook-list"]',
      title: "Journal History List",
      content: "Select from previous journal entries for the current month to view, edit, or delete your notes.",
      position: 'right'
    },
    {
      target: '[data-tour="notebook-editor"]',
      title: "Editor & Trade Information",
      content: "Write remarks in the premium Rich Text Editor, and log your psychological mood, real PNL amount, and your trade count for the day.",
      position: 'left'
    }
  ];

  const [journals, setJournals] = useState<JournalEntry[]>([]);
  const [allNotes, setAllNotes] = useState<JournalEntry[]>([]);
  const [activeJournal, setActiveJournal] = useState<JournalEntry | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [mood, setMood] = useState('');
  const [pnl, setPnl] = useState<number | undefined>();
  const [tradesCount, setTradesCount] = useState<number | undefined>();
  
  // Filter states
  const [activeFolder, setActiveFolder] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePanel, setActivePanel] = useState<'folders' | 'list' | 'editor'>('list');

  // Folders & tags lists
  const [folders, setFolders] = useState<string[]>(['trade', 'daily', 'strategy']);
  const [noteFolder, setNoteFolder] = useState('daily');
  const [noteTags, setNoteTags] = useState<string[]>([]);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [customTagInput, setCustomTagInput] = useState('');

  // Undo delete states
  const [undoTimeoutId, setUndoTimeoutId] = useState<any>(null);
  const [lastDeletedJournal, setLastDeletedJournal] = useState<JournalEntry | null>(null);
  const [showUndoToast, setShowUndoToast] = useState(false);

  // Preset templates
  const PRESET_TEMPLATES = [
    {
      name: "Daily Game Plan",
      content: `<h2>Daily Game Plan</h2>
<p><strong>Market Sentiment:</strong> Neutral</p>
<p><strong>Key Levels to Watch:</strong></p>
<ul>
  <li>Support: $</li>
  <li>Resistance: $</li>
</ul>
<p><strong>Trade Ideas:</strong></p>
<p><strong>Notes:</strong></p>`
    },
    {
      name: "Trade Recap",
      content: `<h2>Trade Recap</h2>
<p><strong>Setup Type:</strong> </p>
<p><strong>Execution Details:</strong> </p>
<p><strong>What went well:</strong> </p>
<p><strong>What to improve:</strong> </p>`
    }
  ];

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('auth_token') || '';
  };

  // Load journals for current month/year
  const loadJournals = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const accountId = selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID ? selectedAccount.id : undefined;
      const now = new Date();
      // Fetch journals. We pass 0 or undefined for CEX account to load all journals
      const response = await apiService.getJournals(now.getFullYear(), now.getMonth() + 1, token, accountId);
      if (response.success) {
        setJournals(response.data || []);
      }
    } catch (err: any) {
      console.error('Error loading journals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync journals
  useEffect(() => {
    const sorted = [...journals].sort((a, b) => {
      return new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime();
    });
    
    setAllNotes(sorted);
    
    // Automatically select the first note if none is active
    if (sorted.length > 0 && !activeJournal) {
      selectJournal(sorted[0]);
    } else if (sorted.length === 0) {
      setActiveJournal(null);
    }
  }, [journals]);

  // Load journals on mount or account change
  useEffect(() => {
    loadJournals();
  }, [selectedAccount]);

  // Select a journal to view/edit
  const selectJournal = (journal: JournalEntry) => {
    setActiveJournal(journal);
    setTitle(journal.title || formatJournalTitle(journal));
    setContent(journal.remarks || '');
    setSelectedDate(journal.trade_date);
    setMood(journal.mood || '');
    setPnl(journal.pnl);
    setTradesCount(journal.trades_count);
    setNoteFolder(journal.folder || 'daily');
    setNoteTags(journal.tags || []);
    setIsEditing(false);
    
    // Auto-navigate to editor panel on mobile when selecting a note
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setActivePanel('editor');
    }
  };

  // Format journal title from date or content
  const formatJournalTitle = (journal: JournalEntry) => {
    if (journal.title) return journal.title;
    const date = new Date(journal.trade_date);
    const monthDay = `${date.getMonth() + 1}/${date.getDate()}`;
    return `${monthDay} Daily Journal`;
  };

  // Create new journal
  const createNewJournal = () => {
    setActiveJournal(null);
    setTitle('');
    setContent('');
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setMood('');
    setPnl(undefined);
    setTradesCount(undefined);
    setNoteFolder(activeFolder !== 'all' ? activeFolder : 'daily');
    setNoteTags([]);
    setIsEditing(true);
    
    // Auto-navigate to editor panel on mobile
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setActivePanel('editor');
    }
  };

  // Save journal to database
  const saveJournal = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        setError('Please login first');
        return;
      }

      // Check date conflicts
      const existingJournal = journals.find(j => j.trade_date === selectedDate);
      if (existingJournal && (!activeJournal || activeJournal.trade_date !== selectedDate)) {
        const confirmOverwrite = confirm(
          `A journal entry already exists for ${selectedDate}. Do you want to overwrite it?`
        );
        if (!confirmOverwrite) {
          setIsSaving(false);
          return;
        }
      }

      const data = {
        cex_account_id: selectedAccount && selectedAccount.id !== ALL_ACCOUNTS_ID ? selectedAccount.id : 0,
        trade_date: selectedDate,
        title: title || 'Untitled Note',
        remarks: content,
        screenshots: [],
        mood: mood || undefined,
        pnl: pnl || undefined,
        trades_count: tradesCount || undefined,
        folder: noteFolder,
        tags: noteTags,
      };

      const response = await apiService.saveJournal(data, token);
      if (response.success) {
        await loadJournals();
        setIsEditing(false);
        
        // Find and select the saved journal
        const savedJournal = response.data;
        setActiveJournal(savedJournal);
        // Manually set state instead of calling selectJournal to avoid
        // re-triggering mobile panel navigation after save
        setTitle(savedJournal.title || formatJournalTitle(savedJournal));
        setContent(savedJournal.remarks || '');
        setSelectedDate(savedJournal.trade_date);
        setMood(savedJournal.mood || '');
        setPnl(savedJournal.pnl);
        setTradesCount(savedJournal.trades_count);
        setNoteFolder(savedJournal.folder || 'daily');
        setNoteTags(savedJournal.tags || []);
        
        // On mobile: stay in editor panel to see saved result
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
          setActivePanel('editor');
        }
        
        setSuccessMessage('Journal saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save journal');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete journal with Undo
  const deleteJournal = async () => {
    if (!activeJournal) return;

    const journalToDelete = { ...activeJournal };
    
    // Optimistic UI update: Remove note from feed immediately
    setAllNotes(prev => prev.filter(n => n.id !== journalToDelete.id));
    setActiveJournal(null);
    setLastDeletedJournal(journalToDelete);
    setShowUndoToast(true);

    // Cancel any previous delete timeout
    if (undoTimeoutId) {
      clearTimeout(undoTimeoutId);
    }

    // Set 8-second timer to permanently delete from backend
    const timeoutId = setTimeout(async () => {
      setShowUndoToast(false);
      try {
        const token = getAuthToken();
        if (!token) return;
        
        const accountId = journalToDelete.cex_account_id || selectedAccount?.id || 0;
        await apiService.deleteJournal(journalToDelete.trade_date, token, accountId);
        await loadJournals();
      } catch (err: any) {
        console.error('Failed to permanently delete journal:', err);
      }
    }, 8000);

    setUndoTimeoutId(timeoutId);
  };

  // Undo delete
  const handleUndoDelete = () => {
    if (undoTimeoutId) {
      clearTimeout(undoTimeoutId);
      setUndoTimeoutId(null);
    }
    
    if (lastDeletedJournal) {
      // Restore note to UI
      setAllNotes(prev => [...prev, lastDeletedJournal].sort((a, b) => new Date(b.trade_date).getTime() - new Date(a.trade_date).getTime()));
      selectJournal(lastDeletedJournal);
    }
    
    setShowUndoToast(false);
    setLastDeletedJournal(null);
  };

  const handleAddFolder = () => {
    const fName = prompt("Enter new folder name:");
    if (!fName) return;
    const cleanFolder = fName.trim().toLowerCase();
    if (!cleanFolder) return;
    if (!folders.includes(cleanFolder)) {
      setFolders(prev => [...prev, cleanFolder]);
    }
    setNoteFolder(cleanFolder);
  };

  const applyTemplate = (templateContent: string) => {
    if (content && content !== '<p><br></p>' && !confirm('Apply template? This will replace your current note content.')) {
      return;
    }
    setContent(templateContent);
  };

  // Format time ago
  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get excerpt from content
  const getExcerpt = (journal: JournalEntry) => {
    if (!journal.remarks) return 'No content available...';
    
    // Strip HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = journal.remarks;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    const cleaned = plainText.trim();
    
    return cleaned.length > 80 ? cleaned.substring(0, 80) + '...' : cleaned || 'No content available...';
  };

  // Get tag list for a journal
  const getTagsForJournal = (journal: JournalEntry) => {
    return journal.tags || [];
  };

  // Dynamic filter application
  const filteredJournals = allNotes.filter(note => {
    // 1. Search Query
    if (searchQuery) {
      const matchTitle = (note.title || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchText = (note.remarks || '').toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchTitle && !matchText) return false;
    }

    // 2. Folder filter
    if (activeFolder !== 'all') {
      if ((note.folder || 'daily') !== activeFolder) return false;
    }

    // 3. Tag filter
    if (selectedTag) {
      const tags = getTagsForJournal(note);
      if (!tags.map(t => t.toUpperCase()).includes(selectedTag.toUpperCase())) return false;
    }

    return true;
  });

  const allCount = allNotes.length;
  const getFolderCount = (fName: string) => {
    return allNotes.filter(n => (n.folder || 'daily') === fName).length;
  };
  const getTagCount = (tName: string) => {
    return allNotes.filter(n => (n.tags || []).some(t => t.toUpperCase() === tName.toUpperCase())).length;
  };

  return (
    <div className={`min-h-screen flex overflow-hidden ${isDarkMode ? 'bg-black text-white' : 'bg-[#FAFAFA] text-black'}`}>
      {/* Sidebar navigation */}
      <Sidebar plan={formData.plan} />

      {/* Main Container */}
      <main className={`flex-1 min-h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`}>
        <TopBar
          title="Notebook"
          name={formData.name}
          showAccountSwitcher={true}
        />
        {/* Action Alerts */}
        {error && (
          <div className={`mx-6 mt-4 p-3 rounded-lg text-xs ${
            isDarkMode 
              ? 'bg-red-500/10 border border-red-500/30 text-red-400' 
              : 'bg-red-50 border border-red-200 text-red-600'
          }`}>
            {error}
          </div>
        )}
        {successMessage && (
          <div className={`mx-6 mt-4 p-3 rounded-lg text-xs flex items-center gap-2 ${
            isDarkMode 
              ? 'bg-green-500/10 border border-green-500/30 text-green-400' 
              : 'bg-green-50 border border-green-200 text-green-600'
          }`}>
            <Check size={14} />
            {successMessage}
          </div>
        )}

        {/* Premium Three Column Layout Grid */}
        <div className="flex-1 flex flex-col md:flex-row md:overflow-hidden pt-16 md:pt-0 pb-14 md:pb-0 h-[calc(100dvh-7.5rem)] max-md:h-[calc(100dvh-7.5rem)] overflow-hidden">
          
          {/* ======================================================== */}
          {/* COLUMN 1: FOLDERS & ALL TAGS SIDEBAR */}
          {/* ======================================================== */}
          <div className={`${activePanel === 'folders' ? 'flex' : 'hidden'} md:flex w-full md:w-[230px] flex-col shrink-0 h-full ${
            isDarkMode 
              ? 'border-r border-[#141414] bg-[#080808]' 
              : 'border-r border-gray-200 bg-white'
          }`}>
             
             {/* Search bar input */}
             <div className={`p-4 ${isDarkMode ? 'border-b border-[#141414]' : 'border-b border-gray-200'}`}>
               <div className="relative">
                 <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-neutral-600' : 'text-gray-400'}`} size={14} />
                 <input 
                   type="text" 
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   placeholder="Search notes..." 
                   className={`w-full rounded-lg py-1.5 pl-8 pr-3 text-[11px] focus:outline-none transition-all ${
                     isDarkMode
                       ? 'bg-[#121212] border border-[#222222] text-white placeholder-neutral-600 focus:border-red-600/50'
                       : 'bg-gray-50 border border-gray-200 text-black placeholder-gray-400 focus:border-red-500/50'
                   }`}
                 />
               </div>
             </div>

             {/* Folders and Tags menus */}
             <div data-tour="notebook-folders" className="p-4 flex-1 overflow-y-auto space-y-6" data-lenis-prevent>
                
                {/* Folders block */}
                <div>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2.5 px-2 flex justify-between items-center ${
                    isDarkMode ? 'text-neutral-500' : 'text-gray-500'
                  }`}>
                    <span>Folders</span>
                    <button 
                      type="button" 
                      onClick={handleAddFolder}
                      className={`text-[10px] font-bold ${isDarkMode ? 'text-neutral-500 hover:text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                      title="Add Custom Folder"
                    >
                      + Add
                    </button>
                  </h3>
                  <nav className="space-y-0.5">
                     <FolderItem 
                       icon={<FileText size={14} />} 
                       label="All Notes" 
                       count={allCount} 
                       active={activeFolder === 'all' && !selectedTag}
                       onClick={() => { setActiveFolder('all'); setSelectedTag(null); }}
                       isDarkMode={isDarkMode}
                     />
                     {folders.map(folder => (
                       <FolderItem 
                         key={folder}
                         icon={
                           folder === 'trade' ? <BarChart2 size={14} /> :
                           folder === 'daily' ? <Calendar size={14} /> :
                           folder === 'strategy' ? <Sparkles size={14} /> :
                           <FileText size={14} />
                         }
                         label={folder.charAt(0).toUpperCase() + folder.slice(1) + (['trade', 'daily', 'strategy'].includes(folder) ? ' Notes' : ' Folder')}
                         count={getFolderCount(folder)}
                         active={activeFolder === folder && !selectedTag}
                         onClick={() => { setActiveFolder(folder); setSelectedTag(null); }}
                         isDarkMode={isDarkMode}
                       />
                     ))}
                  </nav>
                </div>

                {/* All Tags block */}
                <div>
                  <h3 className={`text-[10px] font-black uppercase tracking-widest mb-2.5 px-2 ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>All Tags</h3>
                  <nav className="space-y-0.5">
                     <TagItem 
                       dotColor="bg-[#ff3333]" 
                       label="Loss" 
                       count={getTagCount('Loss')} 
                       active={selectedTag === 'Loss'}
                       onClick={() => setSelectedTag(selectedTag === 'Loss' ? null : 'Loss')}
                       isDarkMode={isDarkMode}
                     />
                     <TagItem 
                       dotColor="bg-[#3b82f6]" 
                       label="Recap" 
                       count={getTagCount('Recap')} 
                       active={selectedTag === 'Recap'}
                       onClick={() => setSelectedTag(selectedTag === 'Recap' ? null : 'Recap')}
                       isDarkMode={isDarkMode}
                     />
                     <TagItem 
                       dotColor="bg-[#eab308]" 
                       label="Breakout" 
                       count={getTagCount('Breakout')} 
                       active={selectedTag === 'Breakout'}
                       onClick={() => setSelectedTag(selectedTag === 'Breakout' ? null : 'Breakout')}
                       isDarkMode={isDarkMode}
                     />
                     <TagItem 
                       dotColor="bg-[#8b5cf6]" 
                       label="Bitcoin" 
                       count={getTagCount('Bitcoin')} 
                       active={selectedTag === 'Bitcoin'}
                       onClick={() => setSelectedTag(selectedTag === 'Bitcoin' ? null : 'Bitcoin')}
                       isDarkMode={isDarkMode}
                     />
                  </nav>
                </div>
             </div>

             {/* Column 1 bottom: Recently Deleted */}
             <div className={`p-3 flex items-center justify-between cursor-pointer transition-colors px-4 ${
               isDarkMode
                 ? 'border-t border-[#141414] bg-[#070707] text-neutral-500 hover:text-neutral-300'
                 : 'border-t border-gray-200 bg-gray-50 text-gray-600 hover:text-gray-900'
             }`}>
               <div className="flex items-center gap-2">
                 <Trash2 size={13} />
                 <span className="text-[11px] font-bold">Recently Deleted</span>
               </div>
             </div>
          </div>

          {/* ======================================================== */}
          {/* COLUMN 2: NOTES FEED / LIST */}
          {/* ======================================================== */}
          <div className={`${activePanel === 'list' ? 'flex' : 'hidden'} md:flex w-full md:w-[300px] flex-col shrink-0 md:max-h-none h-full ${
            isDarkMode 
              ? 'border-r border-[#141414] bg-[#060606]' 
              : 'border-r border-gray-200 bg-gray-50'
          }`}>
             
             {/* Feed Header */}
             <div className={`p-3 flex justify-between items-center shrink-0 ${
               isDarkMode 
                 ? 'border-b border-[#141414] bg-[#050505]' 
                 : 'border-b border-gray-200 bg-white'
             }`}>
                <span className={`text-[10px] font-black uppercase tracking-widest pl-1 ${
                  isDarkMode ? 'text-neutral-400' : 'text-gray-600'
                }`}>
                  {selectedTag ? `${selectedTag} Notes` : activeFolder === 'all' ? 'All Notes' : activeFolder === 'trade' ? 'Trade Notes' : activeFolder === 'daily' ? 'Daily Journal' : activeFolder + ' Folder'}
                </span>
                <button 
                  onClick={createNewJournal}
                  className="bg-[#ef4444] hover:bg-red-700 text-white font-black text-[9px] uppercase tracking-wider py-1 px-3 rounded-full transition-all"
                >
                  + New Note
                </button>
             </div>

             {/* Feed notes list */}
             <div data-tour="notebook-list" className={`flex-1 overflow-y-auto ${isDarkMode ? 'divide-y divide-[#121212]' : 'divide-y divide-gray-200'}`} data-lenis-prevent>
                {filteredJournals.length === 0 && (
                  <div className={`p-8 text-center text-xs mt-12 ${isDarkMode ? 'text-neutral-600' : 'text-gray-400'}`}>
                    No notes found under this folder or tag filter.
                  </div>
                )}
                {filteredJournals.map(note => {
                  const displayTitle = note.title || formatJournalTitle(note);
                  const displayTime = timeAgo(note.updated_at || note.created_at);
                  const tags = getTagsForJournal(note);

                  return (
                    <NoteCard 
                      key={note.id} 
                      title={displayTitle}
                      time={displayTime}
                      excerpt={getExcerpt(note)}
                      tags={tags}
                      active={activeJournal?.id === note.id} 
                      onClick={() => selectJournal(note)}
                      isDarkMode={isDarkMode}
                    />
                  );
                })}
             </div>
          </div>

          {/* ======================================================== */}
          {/* COLUMN 3: NOTE EDITOR & VIEW CANVAS */}
          {/* ======================================================== */}
          <div className={`${activePanel === 'editor' ? 'flex' : 'hidden'} md:flex flex-1 flex-col md:overflow-hidden min-h-0 h-full ${
            isDarkMode ? 'bg-[#030303]' : 'bg-white'
          }`}>
             {activeJournal || isEditing ? (
               <>
                  {/* Top Toolbar Navigation Row */}
                  <div className={`h-12 flex items-center justify-between px-4 md:px-6 shrink-0 ${
                    isDarkMode 
                      ? 'border-b border-[#141414] bg-[#050505]' 
                      : 'border-b border-gray-200 bg-gray-50'
                  }`}>
                     
                     {/* Mobile back button — only visible on mobile */}
                     <button
                       onClick={() => {
                         // If editing, discard and go back
                         if (isEditing && activeJournal) {
                           setIsEditing(false);
                           setTitle(activeJournal.title || formatJournalTitle(activeJournal));
                           setContent(activeJournal.remarks || '');
                         } else if (isEditing && !activeJournal) {
                           // New note discarded, go back to list
                           setIsEditing(false);
                           setActiveJournal(null);
                         }
                         setActivePanel('list');
                       }}
                       className={`md:hidden flex items-center gap-1 transition-colors text-xs font-bold py-2 pr-3 ${
                         isDarkMode ? 'text-neutral-500 hover:text-white' : 'text-gray-600 hover:text-black'
                       }`}
                     >
                       <ChevronLeft size={16} />
                       <span>Notes</span>
                     </button>
                     
                     {/* Desktop spacer */}
                     <div className="hidden md:block" />
                     
                     {/* Editor Control buttons */}
                     <div className="flex items-center gap-2 md:gap-3">
                       <span className={`hidden md:block text-[10px] font-bold ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                         {activeJournal?.updated_at ? `Saved ${timeAgo(activeJournal.updated_at)}` : ''}
                       </span>
                       {activeJournal?.updated_at && <div className={`hidden md:block w-px h-3 ${isDarkMode ? 'bg-neutral-800' : 'bg-gray-300'}`} />}
                       
                       {isEditing ? (
                         <>
                           <button 
                             onClick={() => {
                               setIsEditing(false);
                               if (activeJournal) {
                                 setTitle(activeJournal.title || formatJournalTitle(activeJournal));
                                 setContent(activeJournal.remarks || '');
                               }
                             }}
                             className={`transition-colors p-1 ${isDarkMode ? 'text-neutral-500 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                           >
                             <X size={15} />
                           </button>
                           <button 
                             onClick={saveJournal}
                             disabled={isSaving}
                             className="px-3 md:px-3.5 py-1 bg-green-600 hover:bg-green-700 text-white font-black text-[10px] uppercase tracking-wider rounded-lg transition-colors flex items-center gap-1.5"
                           >
                             <Save size={12} />
                             {isSaving ? 'Saving...' : 'Save'}
                           </button>
                         </>
                       ) : (
                         <>
                           <button 
                             onClick={() => setIsEditing(true)}
                             className="px-3 md:px-3.5 py-1 bg-[#ef4444] hover:bg-red-700 text-white font-black text-[10px] uppercase tracking-wider rounded-lg transition-colors"
                           >
                             Edit Note
                           </button>
                           <button 
                             onClick={deleteJournal}
                             className="text-neutral-600 hover:text-red-500 transition-colors p-1"
                           >
                             <Trash2 size={14} />
                           </button>
                           <button className="text-neutral-600 hover:text-white transition-colors p-1">
                             <MoreVertical size={14} />
                           </button>
                         </>
                       )}
                     </div>
                  </div>

                  {/* Premium Viewer/Editor Canvas container */}
                  <div data-tour="notebook-editor" className="flex-1 overflow-y-auto bg-black" data-lenis-prevent>
                     <div className="max-w-3xl mx-auto px-8 py-10 w-full space-y-6">
                       
                       {/* Big Title Header */}
                       <div>
                         {isEditing ? (
                           <input 
                             type="text" 
                             value={title}
                             onChange={(e) => setTitle(e.target.value)}
                             className="w-full bg-transparent border-none outline-none text-3xl font-black tracking-tight text-white mb-2"
                             placeholder="Note Title..."
                           />
                         ) : (
                           <h1 className="text-3.5xl font-black tracking-tight text-white leading-tight font-sans">
                       {title}
                           </h1>
                         )}
                       </div>

                       {/* Metadata Grid (Styled strictly matching figma specs) */}
                       <div className="border-y border-[#121212] py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                         
                         {/* Created time */}
                         <div className="space-y-1 col-span-1">
                           <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider block">Created</span>
                           <span className="text-neutral-300 font-bold font-mono">
                             {activeJournal ? new Date(activeJournal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Today"}
                           </span>
                         </div>

                         {/* Folder selection */}
                         <div className="space-y-1 col-span-1">
                           <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider block">Folder</span>
                           {isEditing ? (
                             <select
                               value={noteFolder}
                               onChange={(e) => setNoteFolder(e.target.value)}
                               className="bg-neutral-900 border border-neutral-800 text-[10px] text-neutral-300 px-2 py-0.5 rounded font-bold focus:outline-none"
                             >
                               {folders.map(f => (
                                 <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                               ))}
                             </select>
                           ) : (
                             <span className="text-neutral-300 font-bold font-sans">
                               {noteFolder.charAt(0).toUpperCase() + noteFolder.slice(1)}
                             </span>
                           )}
                         </div>

                         {/* Templates Pill Selector */}
                         <div className="space-y-1 col-span-2 sm:col-span-1">
                           <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider block">Templates</span>
                           <div className="flex flex-wrap gap-1 items-center">
                             {PRESET_TEMPLATES.map((tmpl, idx) => (
                               <span 
                                 key={idx}
                                 onClick={() => isEditing && applyTemplate(tmpl.content)}
                                 className={`text-[10px] px-2 py-0.5 rounded font-bold transition-all border ${
                                   isEditing 
                                     ? 'bg-neutral-900 border-neutral-800 text-neutral-300 hover:bg-neutral-800 cursor-pointer' 
                                     : 'bg-neutral-950 border-neutral-900 text-neutral-500 cursor-not-allowed'
                                 }`}
                               >
                                 {tmpl.name}
                               </span>
                             ))}
                           </div>
                         </div>

                         {/* Tags Pill Grid */}
                         <div className="space-y-1 relative col-span-2 sm:col-span-1">
                           <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider block">Tags</span>
                           <div className="flex flex-wrap gap-1 items-center">
                             {noteTags.map((tag, i) => (
                               <span key={i} className="flex items-center gap-1 bg-[#121212] border border-neutral-800 text-[10px] text-neutral-300 px-2 py-0.5 rounded font-bold">
                                 <span className={`w-1.5 h-1.5 rounded-full ${
                                   tag.toUpperCase() === 'LOSS' ? 'bg-[#ff3333]' : 
                                   tag.toUpperCase() === 'RECAP' ? 'bg-[#3b82f6]' : 
                                   tag.toUpperCase() === 'BREAKOUT' ? 'bg-[#eab308]' : 
                                   'bg-[#8b5cf6]'
                                 }`} />
                                 {tag}
                                 {isEditing && (
                                   <button 
                                     type="button" 
                                     onClick={() => setNoteTags(prev => prev.filter(t => t !== tag))} 
                                     className="text-neutral-500 hover:text-red-500 ml-1 font-bold font-sans"
                                   >
                                     &times;
                                   </button>
                                 )}
                               </span>
                             ))}
                             {isEditing && (
                               <div className="relative inline-block">
                                 <button 
                                   type="button"
                                   onClick={() => setShowTagDropdown(!showTagDropdown)}
                                   className="w-5 h-5 bg-[#121212] hover:bg-neutral-900 border border-neutral-800 text-neutral-500 flex items-center justify-center rounded font-bold cursor-pointer transition-all"
                                 >
                                   +
                                 </button>
                                 {showTagDropdown && (
                                   <div className="absolute left-0 sm:left-auto sm:right-0 top-6 bg-[#0f0f0f] border border-neutral-800 rounded-xl p-3 shadow-2xl z-20 w-48 space-y-2">
                                     <span className="text-[9px] font-black uppercase text-neutral-500 block mb-1">Preset Tags</span>
                                     <div className="flex flex-wrap gap-1">
                                       {['LOSS', 'RECAP', 'BREAKOUT', 'BITCOIN'].map(preset => (
                                         <button
                                           key={preset}
                                           type="button"
                                           onClick={() => {
                                             if (!noteTags.includes(preset)) {
                                               setNoteTags(prev => [...prev, preset]);
                                             }
                                             setShowTagDropdown(false);
                                           }}
                                           className="bg-neutral-900 hover:bg-neutral-800 text-neutral-300 border border-neutral-800 px-1.5 py-0.5 rounded text-[9px] font-bold"
                                         >
                                           {preset}
                                         </button>
                                       ))}
                                     </div>
                                     <div className="border-t border-neutral-900 pt-2 space-y-1.5">
                                       <span className="text-[9px] font-black uppercase text-neutral-500 block">Custom Tag</span>
                                       <div className="flex gap-1">
                                         <input
                                           type="text"
                                           value={customTagInput}
                                           onChange={e => setCustomTagInput(e.target.value)}
                                           placeholder="Enter tag..."
                                           className="bg-black border border-neutral-800 rounded px-1.5 py-0.5 text-[10px] w-full text-white focus:outline-none focus:border-red-500"
                                           onKeyDown={e => {
                                             if (e.key === 'Enter') {
                                               e.preventDefault();
                                               const clean = customTagInput.trim().toUpperCase();
                                               if (clean && !noteTags.includes(clean)) {
                                                 setNoteTags(prev => [...prev, clean]);
                                               }
                                               setCustomTagInput('');
                                               setShowTagDropdown(false);
                                             }
                                           }}
                                         />
                                         <button
                                           type="button"
                                           onClick={() => {
                                             const clean = customTagInput.trim().toUpperCase();
                                             if (clean && !noteTags.includes(clean)) {
                                               setNoteTags(prev => [...prev, clean]);
                                             }
                                             setCustomTagInput('');
                                             setShowTagDropdown(false);
                                           }}
                                           className="bg-red-600 hover:bg-red-700 text-white text-[9px] px-2 rounded font-black uppercase"
                                         >
                                           Add
                                         </button>
                                       </div>
                                     </div>
                                   </div>
                                 )}
                               </div>
                             )}
                           </div>
                         </div>
                       </div>

                       {/* Content Render Pane */}
                       <div className="prose prose-invert max-w-none text-neutral-300 font-sans leading-relaxed text-sm">
                          {isEditing ? (
                            <div className="space-y-4">
                              <RichTextEditor
                                value={content}
                                onChange={setContent}
                                disabled={!isEditing}
                                placeholder="Write your trading notes here..."
                              />
                              
                              {/* Mini Forms for quick metadata adjustments during Edit Mode */}
                              <div className="bg-[#0b0b0b] border border-neutral-900 rounded-xl p-4 space-y-3">
                                <h4 className="text-[11px] font-black uppercase tracking-wider text-neutral-400">Trade Metadata (Optional)</h4>
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-[9px] font-black text-neutral-500 block mb-1">MOOD</label>
                                    <input 
                                      type="text" 
                                      value={mood} 
                                      onChange={e => setMood(e.target.value)} 
                                      placeholder="Confident" 
                                      className="w-full bg-[#121212] border border-[#222222] rounded p-1.5 text-xs text-white focus:outline-none focus:border-red-600/50" 
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[9px] font-black text-neutral-500 block mb-1">PNL ($)</label>
                                    <input 
                                      type="number" 
                                      value={pnl || ''} 
                                      onChange={e => setPnl(e.target.value ? parseFloat(e.target.value) : undefined)} 
                                      placeholder="0.00" 
                                      className="w-full bg-[#121212] border border-[#222222] rounded p-1.5 text-xs text-white focus:outline-none focus:border-red-600/50" 
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[9px] font-black text-neutral-500 block mb-1">TRADES</label>
                                    <input 
                                      type="number" 
                                      value={tradesCount || ''} 
                                      onChange={e => setTradesCount(e.target.value ? parseInt(e.target.value) : undefined)} 
                                      placeholder="0" 
                                      className="w-full bg-[#121212] border border-[#222222] rounded p-1.5 text-xs text-white focus:outline-none focus:border-red-600/50" 
                                    />
                                  </div>
                                 </div>
                              </div>
                            </div>
                          ) : (
                            // ========================================================
                            // STANDARD HIGH-FIDELITY VIEWER FOR OTHER AND USER NOTES
                            // ========================================================
                            <div className="space-y-6">
                              <div 
                                className="rich-content font-sans text-sm leading-relaxed text-neutral-300 space-y-4"
                                dangerouslySetInnerHTML={{ __html: content }} 
                              />
                              
                              {/* Bottom performance card if stats are active */}
                              {(pnl !== undefined && pnl !== null) || mood || (tradesCount !== undefined && tradesCount !== null) ? (
                                <div className="bg-[#0b0b0b] border border-neutral-900 rounded-2xl p-5 mt-10 space-y-4 shadow-xl">
                                  <h4 className="text-[11px] font-black uppercase tracking-wider text-neutral-400">Trade Analytics Card</h4>
                                  <div className="grid grid-cols-3 gap-4">
                                    {mood && (
                                      <div className="bg-[#121212] border border-neutral-800/80 rounded-xl p-3">
                                        <span className="text-[9px] font-black uppercase text-neutral-500 block mb-1">Psychological Mood</span>
                                        <span className="text-blue-400 font-black text-sm uppercase">{mood}</span>
                                      </div>
                                    )}
                                    {tradesCount !== undefined && tradesCount !== null && (
                                      <div className="bg-[#121212] border border-neutral-800/80 rounded-xl p-3">
                                        <span className="text-[9px] font-black uppercase text-neutral-500 block mb-1">Trades count</span>
                                        <span className="text-neutral-200 font-mono font-bold text-sm">{tradesCount} executed</span>
                                      </div>
                                    )}
                                    {pnl !== undefined && pnl !== null && (
                                      <div className="bg-[#121212] border border-neutral-800/80 rounded-xl p-3 col-span-1">
                                        <span className="text-[9px] font-black uppercase text-neutral-500 block mb-1">Realized P&L</span>
                                        <span className={`font-mono font-bold text-sm ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                          {pnl >= 0 ? '+' : ''}${pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )}
                       </div>
                     </div>
                  </div>
               </>
             ) : (
               <div className="flex-1 flex items-center justify-center text-neutral-600">
                 <div className="text-center">
                   <FileText size={48} className="mx-auto mb-4 opacity-30 text-neutral-500" />
                   <p className="text-sm font-bold text-neutral-400">No note selected</p>
                   <p className="text-xs text-neutral-600 mt-1">Select a journal from the list or click + New Note to begin.</p>
                 </div>
               </div>
             )}
          </div>
         </div>

        {/* Mobile FAB - New Note — always visible on mobile, floats above bottom tab bar */}
        <button
          onClick={() => {
            createNewJournal();
            // createNewJournal already calls setActivePanel('editor') internally
          }}
          className="md:hidden fixed bottom-16 right-4 z-[55] w-14 h-14 bg-[#ef4444] hover:bg-red-700 text-white rounded-full shadow-2xl shadow-red-900/40 flex items-center justify-center transition-all active:scale-95 border border-red-500/30"
          aria-label="New Note"
        >
          <FilePlus size={22} />
        </button>

        {/* Mobile Bottom Tab Navigation */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#080808] border-t border-[#141414] flex">
          <button
            onClick={() => setActivePanel('folders')}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[9px] font-black uppercase tracking-widest transition-colors ${activePanel === 'folders' ? 'text-[#ef4444]' : 'text-neutral-600'}`}
          >
            <FileText size={18} />
            Folders
          </button>
          <button
            onClick={() => setActivePanel('list')}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[9px] font-black uppercase tracking-widest transition-colors ${activePanel === 'list' ? 'text-[#ef4444]' : 'text-neutral-600'}`}
          >
            <Search size={18} />
            Notes
          </button>
          <button
            onClick={() => {
              if (activeJournal || isEditing) {
                setActivePanel('editor');
              }
              // If no active journal, do nothing — FAB handles new note
            }}
            className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 text-[9px] font-black uppercase tracking-widest transition-colors ${
              activePanel === 'editor' ? 'text-[#ef4444]' : 
              (!activeJournal && !isEditing) ? 'text-neutral-800' : 'text-neutral-600'
            }`}
          >
            <FilePlus size={18} />
            Note
          </button>
        </div>

        {/* Floating Undo delete toast */}
        {showUndoToast && (
          <div className="fixed bottom-6 right-6 bg-[#0c0c0c] border border-red-600/30 rounded-xl px-4 py-3 shadow-2xl flex items-center gap-4 z-50 animate-pulse">
            <span className="text-xs text-neutral-300 font-bold">Note deleted.</span>
            <button 
              onClick={handleUndoDelete}
              className="bg-[#ef4444] hover:bg-red-700 text-white font-black text-[9px] uppercase tracking-wider py-1 px-3 rounded-md transition-all"
            >
              Undo
            </button>
          </div>
        )}

        {/* Product Onboarding Tour overlay */}
        <ProductTour steps={tourSteps} tourKey="notebook" />
      </main>
    </div>
  );
};

// ----------------------------------------------------
// HELPER UI COMPONENTS
// ----------------------------------------------------

const FolderItem = ({ icon, label, count, active = false, onClick, isDarkMode }: any) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
      active 
        ? isDarkMode 
          ? 'bg-[#ef4444]/10 text-white font-bold' 
          : 'bg-red-50 text-black font-bold'
        : isDarkMode
          ? 'text-neutral-500 hover:bg-neutral-900 hover:text-neutral-300'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
     <div className="flex items-center gap-2.5">
        <span className={active ? 'text-[#ef4444]' : isDarkMode ? 'text-neutral-500' : 'text-gray-500'}>{icon}</span>
        <span className="text-[11px]">{label}</span>
     </div>
     <span className={`text-[10px] font-mono ${active ? isDarkMode ? 'text-white' : 'text-black' : isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>{count}</span>
  </button>
);

const TagItem = ({ dotColor, label, count, active = false, onClick, isDarkMode }: any) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg transition-all ${
      active 
        ? isDarkMode 
          ? 'bg-[#ef4444]/10 text-white font-bold' 
          : 'bg-red-50 text-black font-bold'
        : isDarkMode
          ? 'text-neutral-500 hover:bg-neutral-900 hover:text-neutral-300'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
  >
     <div className="flex items-center gap-2.5">
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className="text-[11px]">{label}</span>
     </div>
     <span className={`text-[10px] font-mono ${active ? isDarkMode ? 'text-white' : 'text-black' : isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>{count}</span>
  </button>
);

const NoteCard = ({ title, time, excerpt, tags, active, onClick, isDarkMode }: any) => (
  <div 
    onClick={onClick}
    className={`p-4 cursor-pointer transition-all ${
      active 
        ? isDarkMode 
          ? 'bg-[#121212]/80 border-l-[3px] border-l-[#ef4444]' 
          : 'bg-red-50 border-l-[3px] border-l-[#ef4444]'
        : isDarkMode
          ? 'hover:bg-neutral-900/40'
          : 'hover:bg-gray-50'
    }`}
  >
     <div className="flex justify-between items-start mb-1.5">
        <h4 className={`text-[12px] font-black truncate pr-3 ${active ? isDarkMode ? 'text-white' : 'text-black' : isDarkMode ? 'text-neutral-200' : 'text-gray-900'}`}>{title}</h4>
        <span className={`text-[9px] font-bold shrink-0 font-mono mt-0.5 ${isDarkMode ? 'text-neutral-600' : 'text-gray-500'}`}>{time}</span>
     </div>
     <p className={`text-[10.5px] line-clamp-2 leading-relaxed mb-3 ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>{excerpt}</p>
     <div className="flex flex-wrap gap-1.5">
        {tags.map((tag: string, i: number) => (
           <span key={i} className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${
             tag === 'LOSS' ? 'bg-red-950/40 text-red-400 border border-red-900/30' : 
             tag === 'RECAP' ? isDarkMode ? 'bg-neutral-800/60 text-neutral-300 border border-neutral-700/20' : 'bg-gray-100 text-gray-700 border border-gray-200' : 
             tag === 'BREAKOUT' ? 'bg-amber-950/40 text-amber-400 border border-amber-900/30' :
             isDarkMode ? 'bg-neutral-900 text-neutral-400 border border-neutral-800' : 'bg-gray-100 text-gray-600 border border-gray-200'
           }`}>
              {tag}
           </span>
        ))}
     </div>
  </div>
);

const ToolbarMockIcon = ({ icon, active = false }: any) => (
  <button 
    disabled 
    className={`p-1.5 rounded transition-all cursor-default ${
      active ? 'text-neutral-300 bg-neutral-900' : 'text-neutral-600 hover:text-neutral-500'
    }`}
  >
    {icon}
  </button>
);

export default NotebookPage;
